import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { roleLabel } from '@core/helpers/role-label.helper';
import {
  ChecklistLine,
  ChecklistState,
  Installation,
  getUserFeaturePermissions,
} from '@core/models';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { InstallationProfileService } from '@core/services/installation-profile.service';
import { CardComponent } from '@custom-components/general/card';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TagState } from '@custom-components/general/tag/tag.component';
import { TextButtonComponent } from '@custom-components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import { ROLE_GROUP_ICONS, roleGroupKey } from '../../role-groups';

type RowState = ChecklistState | 'missing';

interface PointRow extends Record<string, unknown> {
  key: string;
  name: string;
  icon: string;
  group: string;
  state: RowState;
  status: string;
  statusState: TagState;
}

// A point that is missing has no source yet, which its icon says rather than its asset.
const MISSING_ICON = 'help_outline';

const GROUP_ORDER = Object.keys(ROLE_GROUP_ICONS);

const TAG_STATES: Record<RowState, TagState> = {
  bound: 'success',
  awaiting_choice: 'warning',
  incomplete: 'error',
  missing: 'error',
};

@Component({
  selector: 'xemex-installation-measurement-points-card',
  imports: [
    CardComponent,
    SkeletonBlockComponent,
    TableComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-measurement-points-card.component.html',
  styleUrl: './installation-measurement-points-card.component.scss',
})
export class InstallationMeasurementPointsCardComponent {
  installation = input.required<Installation>();

  private readonly profileService = inject(InstallationProfileService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);

  // A route that is missing on an older backend answers like an installation the caller cannot
  // see, so a failed read is shown in the card and nowhere else.
  private readonly restarts = signal(0);
  private readonly installationOid = computed(() => this.installation().installationOid);
  private readonly checklist = rxResource({
    params: () => ({ installationOid: this.installationOid(), n: this.restarts() }),
    stream: ({ params }) => this.profileService.checklist(params.installationOid),
  });

  readonly isLoading = computed(() => !this.checklist.hasValue() && this.checklist.isLoading());
  readonly failed = computed(() => !this.checklist.hasValue() && this.checklist.error() != null);

  private readonly permissions = computed(() =>
    getUserFeaturePermissions(this.userStore.user(), this.authStore.permissions(), XEMEX_OID),
  );

  readonly canOpenDataSources = computed(() => this.permissions().includes('view_installations'));

  readonly rows = computed<PointRow[]>(() => {
    const checklist = this.checklist.value();
    if (!checklist) return [];
    const lines = checklist.lines.map(line => this.lineRow(line));
    const withLine = new Set(checklist.lines.map(line => line.role));
    const missing = (this.installation().profile?.roles ?? [])
      .filter(role => role.required && role.state === 'active' && !withLine.has(role.role))
      .map(role => this.missingRow(role.role, role.displayName));
    // One list, in the order the asset groups are listed elsewhere on the installation pages.
    return [...lines, ...missing].sort(
      (a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group),
    );
  });

  // The table fixes its column widths, so the status column is sized to the longest status it shows:
  // about 0.45rem a character, plus the tag's padding and the cell's. The point names get the rest.
  protected readonly columns = computed<TableColumnConfig[]>(() => {
    const longest = Math.max(0, ...this.rows().map(row => row.status.length));
    return [
      {
        field: 'name',
        label: 'installations.checklist.columns.point',
        bold: true,
        iconField: 'icon',
      },
      {
        field: 'status',
        label: 'installations.checklist.columns.status',
        width: `calc(${longest} * 0.45rem + 3.75rem)`,
        type: TableColumnType.Tag,
        stateField: 'statusState',
      },
    ];
  });

  // A read that is still in flight cannot be reloaded, and its answer would be stale, so it is
  // started over instead.
  reload(): void {
    if (!this.checklist.reload()) this.restarts.update(count => count + 1);
  }

  openDataSources(): void {
    void this.router.navigate([
      '/',
      APP_PATHS.Installations,
      this.installationOid(),
      'data-sources',
    ]);
  }

  private lineRow(line: ChecklistLine): PointRow {
    const point = this.installation().points?.find(
      candidate => candidate.pointOid === line.pointOid,
    );
    return this.row(
      line.pointOid,
      point?.name ?? roleLabel(this.translate, line.role, line.role),
      line.role,
      line.state,
    );
  }

  private missingRow(role: string, displayName: string): PointRow {
    return this.row(
      `missing:${role}`,
      roleLabel(this.translate, role, displayName),
      role,
      'missing',
    );
  }

  private row(key: string, name: string, role: string, state: RowState): PointRow {
    const group = roleGroupKey(role);
    return {
      key,
      name,
      icon: state === 'missing' ? MISSING_ICON : ROLE_GROUP_ICONS[group],
      group,
      state,
      status: this.translate.instant(`installations.checklist.state.${state}`),
      statusState: TAG_STATES[state],
    };
  }
}
